import { generateCombinationId } from '~Betslip/utils/combinationsUtils';

import { GTP_BET_BUILDER_COMBINATION_TYPE } from '~Betslip/uiModes/shared/GTPBetBuilder/constants';

/**
 * Base class for sportsbook_live_data subscription tracking.
 * Extend this class to handle new message types.
 * @see gtpBetBuilderSubscriptionStoreClass handles gtp_betbuilder type messages
 */
class sbLiveDataSubscriptionStoreBaseClass {
    /**
     * @var {map} #subscriptions { entityIdentifier -> <object>subscription_data }
     * An entity could be a gtp_betbuilder combination selection (e.g. bb_123_456).
     * The subscription data should be the entire payload needed to send to SDL. It will be spread
     * into the payload (see) this.subscribe()
     */
    #subscriptions = new Map();

    /**
     * @var {string} #subscriptionType an identifier, e.g. gtp_betbuilder. This should be used by
     * the SLD controller to route messages
     */
    #subscriptionType = '';

    constructor(subscriptionType) {
        this.#subscriptionType = subscriptionType;
    }

    /**
     * Update data for a given subscription key
     * @param {string} key unique identifier, e.g. bb_123_456_789 for a gtp_betbuilder selection
     * @param {mixed} data any arbitrary data needed for managing subscriptions for a specific type
     * @returns {boolean|mixed} data that was set or false
     */
    updateSubscription(key, data) {
        if (this.#subscriptions.has(key)) {

            return false;
        }

        this.#subscriptions.set(key, data);

        return data;
    }

    getSubscription(key) {
        return this.#subscriptions.get(key);
    }

    deleteSubscription(key) {
        return this.#subscriptions.delete(key);
    }

    clearSubscriptions() {
        this.#subscriptions = new Map();
    }

    getRegistrations() {
        return this.#subscriptions;
    }

    /**
     * You MUST override this method in your extended store class
     * This function should:
     *  - take a websocket payload that is about to be sent
     *  - check and update existing subscriptions
     *  - filter the payload (for example to exclude subscription payloads for entities we already
     *    receive updates for
     *  - return:
     *      - an updated payload to be sent by the calling code.
     *      or
     *      - false - the calling code does not need to send a message
     * @param {object} payload the websocket message to be sent
     * @returns {boolean|mixed} a filtered payload or false
     */
    subscribe(payload) {
        return {
            action: 'register',
            type: this.#subscriptionType,
            ...payload,
        };
    }

    /**
     * You MUST override this method in your extended store class
     * @see subscribe()
     * @param {object} payload the websocket message to be sent
     * @returns {boolean|mixed} a filtered payload or false
     */
    unsubscribe(payload) {
        return {
            action: 'unregister',
            type: this.#subscriptionType,
            ...payload,
        };
    }

    /**
     * You should override this method to ensure that the websocket payload is correct for this
     * specific type of message - see gtpBetBuilderSubscriptionStoreClass.getReconnectionPayload()
     *
     * Retrieve the payload needed when a websocket connection is established (called in WebSocket
     * onConnection() callback). Note that the store is cleared when you call this method so you
     * generally want to return a subscription payload to your calling code to after calling this
     * method
     * @returns {array} payload to resubscribe - generally an array of individual subscription
     * payloads to be processed by SLD
     */
    getReconnectionPayload() {
        const resubscribePayloadItems = this.getRegistrations();
        this.clearSubscriptions();

        return Array.from(resubscribePayloadItems.values());
    }
}

/**
 * GtpBetBuilder combination type store
 * Do not use this store for any other combination type
 */
class gtpBetBuilderSubscriptionStoreClass extends sbLiveDataSubscriptionStoreBaseClass {
    constructor() {
        super(GTP_BET_BUILDER_COMBINATION_TYPE);
    }

    #getKey(selectionIds) {
        return generateCombinationId({
            combination_type: GTP_BET_BUILDER_COMBINATION_TYPE,
            selections: selectionIds,
        });
    }

    /**
     * Handle a subscription request
     * Parses the payload, updates the subscription store and (maybe) sends a WS message
     * @param {object} payload subscription payload - generally soecific to a specific type
     * @param {boolean} onlyAddToStore if true, do not send a websocket message. Used to avoid
     * sending duplicate messages (e.g.) if a component subscribes and opens a WebSocket connection
     * at the same time, WsInterface.onConnection() will be called.
     * @returns {void}
     */
    subscribe(payload, onlyAddToStore = false) {
        const { combinations: combinationSelectionIds } = payload;
        const filteredPayload = [];
        combinationSelectionIds.forEach(selectionIds => {
            const subscriptionKey = this.#getKey(selectionIds);
            const subscriptionWasAdded = Boolean(this.updateSubscription(subscriptionKey, selectionIds));
            if (subscriptionWasAdded) {
                filteredPayload.push(selectionIds);
            }
        });

        if (!filteredPayload.length || onlyAddToStore) {

            return false;
        }

        return super.subscribe({ combinations: filteredPayload });
    }

    unsubscribe(payload) {
        const { combinations: combinationSelectionIds } = payload;
        if (combinationSelectionIds === null) {
            // special case for gtp bet builder - we can pass null to unsubscribe from all
            const registrations = this.getRegistrations();
            if (registrations.size > 0) {
                this.clearSubscriptions();

                return super.unsubscribe({ combinations: null });
            }

            return false;
        }

        if (!combinationSelectionIds.length) {

            return false;
        }

        const filteredCombinations = [];
        combinationSelectionIds.forEach(selectionIds => {
            const subscriptionKey = this.#getKey(selectionIds);
            const subscriptionExistedAndDeleted = this.deleteSubscription(subscriptionKey);

            if (subscriptionExistedAndDeleted) {
                filteredCombinations.push(selectionIds);
            }
        });

        if (!filteredCombinations.length) {

            return false;
        }

        return super.unsubscribe({ combinations: filteredCombinations });
    }

    getReconnectionPayload() {
        const combinations = super.getReconnectionPayload();
        if (combinations.length === 0) {

            return false;
        }

        // note that we do not update the subscription store here - WsInterface is expected to call
        // it's subscribe method, which calls this.subscribe(), which in turn updates the
        // subscriptions
        return super.subscribe({ combinations });
    }
}

const stores = {
    [GTP_BET_BUILDER_COMBINATION_TYPE]: new gtpBetBuilderSubscriptionStoreClass(),
};

export default stores;
